import { Modal } from "flowbite-react";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { FormikProvider, useFormik } from "formik";
import { useEffect, useState } from "react";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import "./ModalCreatePurchaseOrder.scss";
import { emptyItem, IInvoice, Item } from "../../../../types/invoice.type";
import {
  invoiceNfeSigle,
  invoicesNfeCompleted,
} from "../../../../common/services/invoice.api";
import InputFormik from "../../../../common/components/form/InputFormik";
import { formatDate } from "../../../../common/helpers/date.helpers";
import GenericTableSelect from "../../../../common/components/ui/table/GenericTableSelect";
import { GenericInfoField } from "../../../../common/helpers/generic-info-field";
import * as Yup from "yup";
import "./ModalConfirmNfe.scss";
import { IProduct } from "../../../../types/product.types";
import { getAllProducts } from "../../../../common/services/product.api";
import { getCompanyId } from "../../../../common/services/company.api";
import { ICompanyAccess } from "../../../../types/companyaccess.type";
import { createStock } from "../../../../common/services/stock.api";
import {
  getPurchaseOrderById,
  purchaseOrderInventoryLaunched,
} from "../../../../common/services/purchaseOrder.api";
import AutocompleteFormik from "../../../../common/components/form/AutocompleteFormik";
import { RxCross2 } from "react-icons/rx";

type Props = {
  onClose?: () => void;
  purchaseOrderId: string;
};

export default function ModalConfirmNfe(props: Props) {
  useToastContext();
  const [loading, setLoading] = useState(false);
  const [nfs, setNfs] = useState<IInvoice[]>([]);
  const [itens, setItens] = useState<Item[]>([]);
  const [invoice, setInvoice] = useState<IInvoice>();
  const [products, setProducts] = useState<IProduct[]>([]);
  const { notifyError, notifySuccess } = useToastContext();
  const [isFrac, setIsFrac] = useState(false);
  const [loadingFrac, setLoadingFrac] = useState(false);

  const selectItem = async (invoice: IInvoice) => {
    const result = await invoiceNfeSigle(invoice._id as string);
    setItens(result.data);
    setInvoice(invoice);
  };

  useEffect(() => {
    async function loadProducts() {
      const result = await getAllProducts();
      setProducts(result.data);
    }
    loadProducts();
  }, []);

  const handleCloseModal = () => {
    props.onClose && props.onClose();
  };

  const initialValues = {};

  const createSchema = () => {
    const schema = {};

    new Array(itens.length).fill(null).map((_, index) => {
      schema[`product${index}`] = Yup.object().required(
        "Produto é obrigatorio"
      );
    });

    return Yup.object().shape(schema);
  };

  const parseMoneyStringToNumber = (value) => {
    if (typeof value == "number") {
      return value;
    }
    if (!value) {
      return 0;
    }
    return parseFloat(
      value
        .replace("R$", "")
        .replace(/\s/g, "")
        .replace(".", "")
        .replace(",", ".")
    );
  };

  const releaseWithoutDfe = async () => {
    const result = await getPurchaseOrderById(props.purchaseOrderId);
    setItens(
      result.data.itens.map((item) => {
        return {
          ...emptyItem(),
          descricao: item.description,
          valor_bruto: String(parseMoneyStringToNumber(item.value)),
          valor_unitario_comercial: String(
            parseMoneyStringToNumber(item.value)
          ),
          quantidade_comercial: String(item.qtd),
          numero_item: String(item.qtd),
        };
      })
    );
  };

  const formatFormikData = async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any[] = [];
    for (let index = 0; index < itens.length; index++) {
      const productId = formik.values[`product${index}`].value;
      const quantity =
        Number(verifyItem(itens[index]).quantity) *
        (isFrac ? Number(formik.values[`frac${index}`] || 1) : 1);

      const supplierName =
        invoice?.nome_emitente ||
        (await getPurchaseOrderById(props.purchaseOrderId).then(
          (res) => res.data.supplierName
        ));
      if (!supplierName) {
        notifyError("Fornecedor não encontrado");
        return;
      }
      const invoiceId = invoice?._id;
      const costPrice =
        Number(verifyItem(itens[index]).price) /
        (isFrac ? Number(formik.values[`frac${index}`] || 1) : 1);

      const storedCompany = window.localStorage.getItem("selectedCompanies");
      const company: ICompanyAccess | null = storedCompany
        ? JSON.parse(storedCompany)
        : null;
      if (!company) {
        notifyError("Company not-found in localstorage");
        return;
      }

      const taxBurdens = company.taxBurdens;

      const totalTaxBurdens =
        taxBurdens.ADIC +
        taxBurdens.COFINS +
        taxBurdens.CSLL +
        taxBurdens.IRPJ +
        taxBurdens.ISS +
        taxBurdens.PIS;

      data.push({
        companyId: getCompanyId(),
        productId: productId,
        minimumQuantity: 10,
        invoiceId: invoiceId,
        details: [
          {
            quantity: quantity,
            costPrice: costPrice,
            sellPrice: costPrice * (1 + totalTaxBurdens / 100),
            supplierName: supplierName,
            costFreight: 0,
          },
        ],
      });
    }
    return data;
  };

  const formik = useFormik({
    initialValues,
    validationSchema: createSchema,
    onSubmit: async () => {
      setLoading(true);
      const data = await formatFormikData();

      const resultCreateStock = await createStock({ stocks: data });
      if (!resultCreateStock.ok) {
        notifyError(resultCreateStock.message as string);
        return;
      }

      const resultPurchaseOrder = await purchaseOrderInventoryLaunched(
        props.purchaseOrderId
      );
      if (!resultPurchaseOrder.ok) {
        notifyError(resultPurchaseOrder.message as string);
      } else {
        notifySuccess("Lançado Estoque com Sucesso");
      }

      handleCloseModal();
    },
  });

  async function loadNfs(search) {
    setLoading(true);
    const result = await invoicesNfeCompleted(search);
    setLoading(false);
    setNfs(result.data);
  }

  async function eventLoadingFrac() {
    setIsFrac(true);
    setLoadingFrac(true);
    await new Promise((resolve) => setTimeout(() => resolve(null), 2000));
    setLoadingFrac(false);
  }

  const verifyItem = (item: Item) => {
    const price = Number(item.valor_bruto);
    const unit_price = Number(item.valor_unitario_comercial);

    if (price > unit_price) {
      return { quantity: Number(item.quantidade_comercial), price: unit_price };
    }
    return { quantity: item.numero_item, price: price };
  };

  return (
    <Modal
      className="relative text-black"
      show={true}
      onClose={() => handleCloseModal()}
    >
      <Modal.Header className="text-black dark:text-black">
        <h2 className="text-black">Lançamento</h2>
        <h2 className="text-black">
          {!invoice
            ? "Selecione uma NFe"
            : `Dados da NFe: ${invoice.nome_emitente}`}
        </h2>
      </Modal.Header>
      <Modal.Body className="text-black dark:text-black ">
        <FormikProvider value={formik}>
          {!itens.length ? (
            <div>
              <InputFormik
                className="mb-4"
                onChange={(e) => loadNfs(e.target.value)}
                name="search"
                type="text"
              ></InputFormik>
              <div className="container-stock">
                {!nfs.length &&
                window?.document?.querySelectorAll("input")[3]?.value?.length &&
                !loading ? (
                  <div className="confirmWithoutDfe">
                    <h2>
                      Não achamos a NFe, deseja fazer o lançamento pela Ordem de
                      Compra ?
                    </h2>
                    <button
                      onClick={releaseWithoutDfe}
                      className="bg-redPrimary"
                    >
                      Lança Estoque
                    </button>
                  </div>
                ) : (
                  <GenericTableSelect
                    data={nfs}
                    renders={{
                      data_emissao: (data) => <p>{formatDate(data)}</p>,
                    }}
                    load={loading}
                    onClick={selectItem}
                    headers={{
                      nome_emitente: "Nome Eminente",
                      valor_total: "Valor Total",
                      data_emissao: "Data da Emissão",
                    }}
                  ></GenericTableSelect>
                )}
              </div>
            </div>
          ) : (
            <div>
              {itens.map((item, index) => {
                return (
                  <div className="container-item-nfe">
                    <div>
                      <GenericInfoField
                        size={item.descricao.length}
                        id={`${item.descricao}${index}`}
                        label="Descrição"
                        spanSize={2}
                        value={item.descricao}
                      ></GenericInfoField>
                      <GenericInfoField
                        size={item.descricao.length}
                        id={`${item.unidade_tributavel}${index}`}
                        label="Quantidade"
                        spanSize={2}
                        value={verifyItem(item).quantity}
                      ></GenericInfoField>
                      <GenericInfoField
                        id={`${item.descricao}${index}`}
                        label="Valor"
                        spanSize={2}
                        value={verifyItem(item).price}
                      ></GenericInfoField>
                    </div>
                    <div className="ml-1 select-product">
                      <AutocompleteFormik
                        className="w-full mb-2"
                        name={`product${index}`}
                        options={[
                          { label: "", value: "" },
                          ...products.map((product) => ({
                            label: product.name,
                            value: product._id,
                          })),
                        ]}
                      ></AutocompleteFormik>
                      <div className="frac">
                        <InputFormik
                          type="number"
                          name={`frac${index}`}
                        ></InputFormik>
                        <ButtonWithLoader
                          notVerifyDoubleClick={true}
                          isLoading={loadingFrac}
                          onClick={() => eventLoadingFrac()}
                        >
                          Fracionar
                        </ButtonWithLoader>
                      </div>
                    </div>
                    <RxCross2
                      className="dropItem"
                      onClick={() => {
                        setItens(itens.filter((_, i) => i != index));
                      }}
                    ></RxCross2>
                  </div>
                );
              })}
              <div className="text-center mt-2">
                <ButtonWithLoader
                  disabled={
                    Object.keys(formik.errors).length ||
                    !formik.values["product0"]
                      ? true
                      : false
                  }
                  type="submit"
                  isLoading={loading}
                  onClick={formik.handleSubmit}
                >
                  Lança Estoque
                </ButtonWithLoader>
              </div>
            </div>
          )}
        </FormikProvider>
      </Modal.Body>
    </Modal>
  );
}
